import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { GBSCError } from "@/models/GBSCError";
import {
  IStaffReportInfo,
  IStaffReportStatusResponse,
} from "@/models/ApiRequestResponse/report";

import {
  getStaffReportApi,
  patchStaffReportStatus,
} from "@/utils/apis/doctor/report";

import { RootState } from "@/store/store";

export const fetchStaffReport = createAsyncThunk<IStaffReportInfo, void>(
  "staffReport/fetch",
  async () => {
    const response = await getStaffReportApi();
    if ("result" in response) {
      return response;
    }
    throw new Error("Failed to fetch staff report");
  }
);

export const patchStaffReportStatusThunk = createAsyncThunk<
  IStaffReportStatusResponse,
  { id: string; is_active: boolean }
>("staffReport/patchStatus", async ({ id, is_active }) => {
  const response = await patchStaffReportStatus({ id, is_active });

  if (!(response instanceof GBSCError)) {
    return response;
  }

  throw response;
});

interface StaffReportState {
  data: IStaffReportInfo | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  patchStatus: "idle" | "loading" | "succeeded" | "failed";
  patchError: string | null;
  patchResult: boolean | null;
}

const initialState: StaffReportState = {
  data: null,
  status: "idle",
  error: null,
  patchStatus: "idle",
  patchError: null,
  patchResult: null,
};

const staffReportSlice = createSlice({
  name: "staffReport",
  initialState,
  reducers: {
    resetReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffReport.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchStaffReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchStaffReport.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch report";
      })
      .addCase(patchStaffReportStatusThunk.pending, (state) => {
        state.patchStatus = "loading";
        state.patchError = null;
        state.patchResult = null;
      })
      .addCase(patchStaffReportStatusThunk.fulfilled, (state, action) => {
        state.patchStatus = "succeeded";
        state.patchResult = action.payload.result;
        state.patchError = null;
        if (state.data?.result) {
          state.data.result = state.data.result.map((staff) =>
            staff.user_id === action.payload.id
              ? { ...staff, is_active: action.payload.is_active }
              : staff
          );
        }
      })
      .addCase(patchStaffReportStatusThunk.rejected, (state, action) => {
        state.patchStatus = "failed";
        state.patchError = action.error.message || "Failed to patch status";
      });
  },
});

export const { resetReport } = staffReportSlice.actions;

const selectStaffReport = (state: RootState) => state.staff;

export const staffReportSelector = createSelector(
  [selectStaffReport],
  (staffReport) => ({
    data: staffReport.data,
    status: staffReport.status,
    error: staffReport.error,
    patchStatus: staffReport.patchStatus,
    patchError: staffReport.patchError,
    patchResult: staffReport.patchResult,
  })
);

export default staffReportSlice.reducer;
